import React from 'react';
import {StyleSheet, View} from 'react-native';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {invalidateScheduleDependent} from '@/entities/attendance/cache';
import {applyDays, undoDays, type DayChoice} from '@/entities/calendar/apply';
import type {CalendarException} from '@/entities/calendar/types';
import {messageOf} from '@/shared/lib/errors';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {AppText} from '@/shared/ui/text';
import {toast} from '@/shared/ui/toast';
import {radii, spacing, useTheme, withAlpha} from '@/shared/ui/theme';

/**
 * Что сделать с выделенными днями — одной кнопкой для всех.
 *
 * Действия применяются сразу, без подтверждения: любое из них обратимо тем же
 * баром («Как по графику» снимает свои записи), а лишний диалог на каждое «сделать
 * эти пять дней выходными» — это то неудобство, ради которого выделение и делалось.
 * Название праздника задаётся в «Подробнее…» — оно есть, когда выбран один день.
 */
const styles = StyleSheet.create({
  root: {borderRadius: radii.lg, borderWidth: 1, padding: spacing(3), gap: spacing(2)},
  // Высота строки — под кнопку «Снять»: пустая панель той же высоты, что и полная.
  head: {flexDirection: 'row', alignItems: 'center', gap: spacing(2), flexWrap: 'wrap', minHeight: 36},
  count: {flex: 1, minWidth: 120},
  actions: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing(2)},
});

export function SelectionBar({
  companyId,
  departmentId,
  dates,
  exceptions,
  onDone,
  onClear,
  onDetails,
}: {
  companyId: string;
  departmentId?: string;
  dates: ReadonlyArray<string>;
  exceptions: ReadonlyArray<CalendarException>;
  /** После записи: снять выделение. */
  onDone: () => void;
  onClear: () => void;
  /** Есть, когда выбран ровно один день: открыть лист дня с названием. */
  onDetails?: () => void;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  const qc = useQueryClient();

  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({queryKey: ['calendar', companyId]}),
      invalidateScheduleDependent(qc),
    ]);

  const apply = useMutation({
    mutationFn: (choice: DayChoice) =>
      applyDays(companyId, departmentId, [...dates].sort(), choice, '', exceptions),
    onSuccess: async applied => {
      await refresh();
      onDone();
      // «Отменить» настоящая: возвращает на датах ровно то, что было до нажатия.
      toast.success(t('schedule.cal.saved', {count: applied.count}), {
        action: {
          label: t('actions.undo'),
          onPress: () => {
            void undoDays(companyId, departmentId, applied)
              .then(refresh)
              .catch(e => toast.error(messageOf(e, t('errors.unknown'))));
          },
        },
      });
    },
  });

  const busy = apply.isPending;
  const none = dates.length === 0;
  const action = (choice: DayChoice, label: string, testID: string) => (
    <AppButton
      key={choice}
      title={label}
      tone={choice === 'schedule' ? 'outline' : 'soft'}
      size="sm"
      onPress={() => apply.mutate(choice)}
      loading={busy && apply.variables === choice}
      disabled={busy || none}
      testID={testID}
    />
  );

  return (
    <View
      // Стоит всегда, пустая — с подсказкой: появляясь на первом выделении, она
      // сдвигала календарь вниз прямо под рукой.
      style={[
        styles.root,
        none
          ? {borderColor: p.border, backgroundColor: p.card}
          : {borderColor: p.primary, backgroundColor: withAlpha(p.primary, 0.08)},
      ]}
      testID="selection-bar">
      <View style={styles.head}>
        <AppText variant={none ? 'caption' : 'label'} style={styles.count}>
          {none ? t('schedule.cal.selectHint') : t('schedule.cal.selected', {count: dates.length})}
        </AppText>
        {onDetails ? (
          <AppButton
            title={t('schedule.cal.details')}
            tone="ghost"
            size="sm"
            onPress={onDetails}
            disabled={busy}
          />
        ) : null}
        {none ? null : (
          <AppButton
            title={t('schedule.cal.clear')}
            tone="ghost"
            size="sm"
            icon="close"
            onPress={onClear}
            disabled={busy}
            testID="selection-clear"
          />
        )}
      </View>
      <View style={styles.actions}>
        {action('holiday', t('calendar.kind.holiday'), 'apply-holiday')}
        {action('day_off', t('calendar.kind.day_off'), 'apply-day-off')}
        {action('workday', t('calendar.kind.workday'), 'apply-workday')}
        {action('schedule', t('schedule.cal.choice.schedule'), 'apply-schedule')}
      </View>
      {apply.isError ? (
        <Banner tone="error" title={messageOf(apply.error, t('errors.unknown'))} />
      ) : null}
    </View>
  );
}
